import { motion } from "framer-motion";
import { Home, MessageSquare, Radio, Settings, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useSubscription } from "@/api/billing";
import { TrialOfferSheet } from "@/components/trial/TrialOfferSheet";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

const tabs = [
  { to: "/app", label: "Главная", icon: Home },
  { to: "/app/chats", label: "Чаты", icon: MessageSquare },
  { to: "/app/mailing", label: "Рассылка", icon: Radio },
  { to: "/app/accounts", label: "Аккаунты", icon: Users },
  { to: "/app/settings", label: "Настройки", icon: Settings },
] as const;

export function AppShell() {
  const { data: subscription } = useSubscription();
  const [trialOpen, setTrialOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Auto-open the trial offer once subscription state has loaded and the user
  // is eligible. We re-check on every state change; the server's `trial_granted`
  // fuse flips after activation so this naturally stops opening.
  useEffect(() => {
    if (!subscription) return;
    if (subscription.trial_available && !dismissed) {
      setTrialOpen(true);
    } else {
      setTrialOpen(false);
    }
  }, [subscription, dismissed]);

  return (
    <div className="min-h-[100dvh] flex flex-col">
      <main className="flex-1">
        <Outlet />
      </main>
      <BottomNav />
      <TrialOfferSheet
        open={trialOpen}
        onClose={() => {
          setTrialOpen(false);
          setDismissed(true);
        }}
      />
    </div>
  );
}

function BottomNav() {
  const location = useLocation();

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 pointer-events-none"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto max-w-md px-4 pb-3 pointer-events-auto">
        <div
          className={cn(
            "relative h-[58px] rounded-[22px] glass-strong",
            "flex items-center justify-around px-1",
            "shadow-[0_10px_40px_-12px_rgba(0,0,0,0.6)]",
          )}
        >
          {tabs.map((tab) => {
            const active = location.pathname === tab.to;
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                onClick={() => haptic.select()}
                className="relative flex-1 h-full grid place-items-center"
                aria-label={tab.label}
              >
                {active && (
                  <motion.div
                    layoutId="nav-pill"
                    className="absolute inset-1 rounded-2xl bg-gradient-accent-soft border border-accent/20"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <Icon
                  className={cn(
                    "relative size-5 transition-colors",
                    active ? "text-accent" : "text-text-dim",
                  )}
                  strokeWidth={active ? 2.4 : 2}
                />
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
